import { useState, useCallback } from 'react'

export type TriFormatField = 'qty' | 'pct' | 'dollar'

interface TriFormatValues {
  qty: string
  pct: string
  dollar: string
}

interface UseTriFormatReturn {
  /** Current values for all three fields */
  values: TriFormatValues
  /** Which field was last edited (for UI highlighting) */
  activeField: TriFormatField
  /** Update a field and auto-calculate others */
  updateField: (field: TriFormatField, value: string) => void
  /** Reset all values */
  reset: () => void
  /** Set all values directly (for edit mode) */
  setValues: (values: TriFormatValues) => void
}

/**
 * Hook for tri-format input auto-calculation
 * 
 * When user enters one field (qty, pct, or dollar), automatically
 * calculates the other two based on reference price and current quantity.
 * 
 * @param referencePrice - T-1 reference price for the asset (used for calculations)
 * @param currentQuantity - Current position quantity (base for pct calculations)
 * @returns Tri-format state and update functions
 * 
 * @example
 * ```tsx
 * const { values, updateField, activeField } = useTriFormat(65000, 1.5)
 * 
 * // When user enters qty
 * updateField('qty', '2.0')
 * // values becomes: { qty: '2.0', pct: '33.33', dollar: '130000.00' }
 * ```
 */
export function useTriFormat(
  referencePrice: number,
  currentQuantity: number
): UseTriFormatReturn {
  const [values, setValues] = useState<TriFormatValues>({
    qty: '',
    pct: '',
    dollar: '',
  })
  const [activeField, setActiveField] = useState<TriFormatField>('qty')

  /**
   * Calculate all three values from a single input
   */
  const calculate = useCallback(
    (field: TriFormatField, inputValue: string): TriFormatValues => {
      // Handle empty input
      if (!inputValue || inputValue === '') {
        return { qty: '', pct: '', dollar: '' }
      }

      const numValue = parseFloat(inputValue)
      if (isNaN(numValue)) {
        return { qty: inputValue, pct: '', dollar: '' }
      }

      // Guard against zero reference price or current quantity
      if (referencePrice <= 0 || currentQuantity <= 0) {
        return {
          qty: field === 'qty' ? inputValue : '',
          pct: field === 'pct' ? inputValue : '',
          dollar: field === 'dollar' ? inputValue : '',
        }
      }

      switch (field) {
        case 'qty': {
          // Input: new quantity
          // Calculate pct change and dollar value
          const newQty = numValue
          const pctChange = ((newQty - currentQuantity) / currentQuantity) * 100
          const dollarValue = newQty * referencePrice

          return {
            qty: newQty.toFixed(4),
            pct: pctChange.toFixed(2),
            dollar: dollarValue.toFixed(2),
          }
        }

        case 'pct': {
          // Input: percentage change
          // Calculate new quantity and dollar value
          const pctChange = numValue
          const newQty = currentQuantity * (1 + pctChange / 100)
          const dollarValue = newQty * referencePrice

          return {
            qty: newQty.toFixed(4),
            pct: inputValue,
            dollar: dollarValue.toFixed(2),
          }
        }

        case 'dollar': {
          // Input: dollar value
          // Calculate quantity and percentage
          const dollarValue = numValue
          const newQty = dollarValue / referencePrice
          const pctChange = ((newQty - currentQuantity) / currentQuantity) * 100

          return {
            qty: newQty.toFixed(4),
            pct: pctChange.toFixed(2),
            dollar: inputValue,
          }
        }

        default:
          return { qty: '', pct: '', dollar: '' }
      }
    },
    [referencePrice, currentQuantity]
  )

  /**
   * Update a specific field and recalculate others
   */
  const updateField = useCallback(
    (field: TriFormatField, value: string) => {
      setActiveField(field)
      const newValues = calculate(field, value)
      setValues(newValues)
    },
    [calculate]
  )

  /**
   * Reset all values to empty
   */
  const reset = useCallback(() => {
    setValues({ qty: '', pct: '', dollar: '' })
    setActiveField('qty')
  }, [])

  /**
   * Set values directly (for edit mode initialization)
   */
  const setValuesDirectly = useCallback((newValues: TriFormatValues) => {
    setValues(newValues)
  }, [])

  return {
    values,
    activeField,
    updateField,
    reset,
    setValues: setValuesDirectly,
  }
}

/**
 * Validate tri-format values
 * Ensures at least one field has a valid numeric value
 */
export function validateTriFormatValues(values: TriFormatValues): boolean {
  const qty = parseFloat(values.qty)
  const pct = parseFloat(values.pct)
  const dollar = parseFloat(values.dollar)

  return !isNaN(qty) || !isNaN(pct) || !isNaN(dollar)
}

/**
 * Convert tri-format values to a position change delta
 * Returns the quantity delta (new qty - current qty)
 */
export function getQuantityDeltaFromTriFormat(
  values: TriFormatValues,
  referencePrice: number,
  currentQuantity: number
): number {
  // Priority: qty > pct > dollar
  if (values.qty) {
    const newQty = parseFloat(values.qty)
    if (!isNaN(newQty)) {
      return newQty - currentQuantity
    }
  }

  if (values.pct) {
    const pct = parseFloat(values.pct)
    if (!isNaN(pct)) {
      const newQty = currentQuantity * (1 + pct / 100)
      return newQty - currentQuantity
    }
  }

  if (values.dollar && referencePrice > 0) {
    const dollar = parseFloat(values.dollar)
    if (!isNaN(dollar)) {
      const newQty = dollar / referencePrice
      return newQty - currentQuantity
    }
  }

  return 0
}
